import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { Plus, Pencil, Trash2, X } from 'lucide-react'

import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type { Recommender } from '@/types/models'

// ---------------------------------------------------------------------------
// RecommenderModal
// ---------------------------------------------------------------------------

interface ModalProps {
  recommender: Recommender | null
  isOpen: boolean
  onClose: () => void
  workspaceId: string
}

function RecommenderModal({ recommender, isOpen, onClose, workspaceId }: ModalProps) {
  const queryClient = useQueryClient()
  const isEdit = !!recommender

  const [name, setName] = useState(recommender?.name ?? '')
  const [email, setEmail] = useState(recommender?.email ?? '')
  const [title, setTitle] = useState(recommender?.title ?? '')
  const [institution, setInstitution] = useState(recommender?.institution ?? '')
  const [relationship, setRelationship] = useState(recommender?.relationship ?? '')
  const [phoneNumber, setPhoneNumber] = useState(recommender?.phoneNumber ?? '')
  const [notes, setNotes] = useState(recommender?.notes ?? '')
  const [error, setError] = useState('')

  // Reset fields when recommender prop changes
  function resetForm(rec: Recommender | null) {
    setName(rec?.name ?? '')
    setEmail(rec?.email ?? '')
    setTitle(rec?.title ?? '')
    setInstitution(rec?.institution ?? '')
    setRelationship(rec?.relationship ?? '')
    setPhoneNumber(rec?.phoneNumber ?? '')
    setNotes(rec?.notes ?? '')
    setError('')
  }

  const mutation = useMutation({
    mutationFn: (data: Partial<Recommender>) =>
      isEdit
        ? api.patch<ApiResponse<Recommender>>(`/v1/recommenders/${recommender!.id}`, data)
        : api.post<ApiResponse<Recommender>>('/v1/recommenders', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recommenders', workspaceId] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.')
      return
    }
    setError('')
    mutation.mutate({
      name: name.trim(),
      email: email.trim(),
      title: title.trim() || undefined,
      institution: institution.trim() || undefined,
      relationship: relationship.trim() || undefined,
      phoneNumber: phoneNumber.trim() || undefined,
      notes: notes.trim() || undefined,
    })
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      resetForm(recommender)
      onClose()
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-6 shadow-xl focus:outline-none">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-base font-semibold text-foreground">
              {isEdit ? 'Edit Recommender' : 'Add Recommender'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md p-1 text-muted-foreground hover:bg-accent"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Name *</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Prof. John Smith"
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Email *</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@university.edu"
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Title</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Associate Professor"
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Institution</span>
                <input
                  type="text"
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                  placeholder="MIT"
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Relationship</span>
                <input
                  type="text"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  placeholder="Research supervisor"
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Phone (optional)</span>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1 555 000 0000"
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs font-medium text-muted-foreground">Notes</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Any notes about this recommender..."
                  className="resize-none rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={mutation.isPending}
              className="w-full rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {mutation.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Recommender'}
            </button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ---------------------------------------------------------------------------
// DeleteConfirmDialog
// ---------------------------------------------------------------------------

interface DeleteProps {
  recommender: Recommender | null
  isOpen: boolean
  onClose: () => void
  workspaceId: string
}

function DeleteConfirmDialog({ recommender, isOpen, onClose, workspaceId }: DeleteProps) {
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/v1/recommenders/${recommender!.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recommenders', workspaceId] })
      onClose()
    },
  })

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-6 shadow-xl focus:outline-none">
          <Dialog.Title className="text-base font-semibold text-foreground">
            Delete Recommender
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-muted-foreground">
            Are you sure you want to remove{' '}
            <strong>{recommender?.name}</strong>? This cannot be undone.
          </Dialog.Description>
          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="flex-1 rounded-md bg-destructive px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ---------------------------------------------------------------------------
// RecommendersPage
// ---------------------------------------------------------------------------

/**
 * RecommendersPage — manage letter-of-recommendation contacts.
 *
 * Grid of recommender cards with Add / Edit / Delete actions.
 */
export default function RecommendersPage() {
  const { currentWorkspaceId } = useWorkspaceStore()
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Recommender | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Recommender | null>(null)

  const { data, isLoading } = useQuery<ApiResponse<Recommender[]>>({
    queryKey: ['recommenders', currentWorkspaceId],
    queryFn: () => api.get<ApiResponse<Recommender[]>>('/v1/recommenders'),
    enabled: !!currentWorkspaceId,
  })
  const recommenders = data?.data ?? []

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Recommenders</h1>
        <button
          type="button"
          onClick={() => setAddModalOpen(true)}
          className="flex items-center gap-2 rounded-md bg-brand-blue px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          Add Recommender
        </button>
      </div>

      {/* Content */}
      {isLoading && (
        <div className="text-center py-12 text-sm text-muted-foreground">Loading...</div>
      )}

      {!isLoading && recommenders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-muted-foreground">No recommenders yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add the professors or managers who will write your letters.
          </p>
          <button
            type="button"
            onClick={() => setAddModalOpen(true)}
            className="mt-4 flex items-center gap-2 rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            Add Recommender
          </button>
        </div>
      )}

      {recommenders.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {recommenders.map((rec) => (
            <div key={rec.id} className="rounded-xl border bg-card p-4 shadow-sm">
              {/* Card header */}
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-foreground truncate">{rec.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{rec.email}</p>
                </div>
                <div className="ml-2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditTarget(rec)}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(rec)}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Card body */}
              {rec.title && (
                <p className="mt-2 text-sm text-foreground">{rec.title}</p>
              )}
              {rec.institution && (
                <p className="text-sm text-muted-foreground">{rec.institution}</p>
              )}
              {rec.relationship && (
                <span className="mt-2 inline-block rounded-full bg-brand-blue/10 px-2 py-0.5 text-xs font-medium text-brand-blue">
                  {rec.relationship}
                </span>
              )}
              {rec.notes && (
                <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{rec.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit modal */}
      <RecommenderModal
        recommender={null}
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        workspaceId={currentWorkspaceId ?? ''}
      />
      <RecommenderModal
        recommender={editTarget}
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        workspaceId={currentWorkspaceId ?? ''}
      />

      {/* Delete confirm */}
      <DeleteConfirmDialog
        recommender={deleteTarget}
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        workspaceId={currentWorkspaceId ?? ''}
      />
    </div>
  )
}
